import React from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: LucideIcon;
  badge?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  variant?: 'default' | 'emerald' | 'crimson' | 'amber' | 'cyan';
  className?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  badge,
  trend,
  trendValue,
  variant = 'default',
  className,
}) => {
  const variantStyles = {
    default: 'border-[var(--border-default)] bg-[var(--bg-surface)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-surface-elevated)]',
    emerald: 'border-emerald-900/40 bg-emerald-950/20 hover:border-emerald-800/70 hover:bg-emerald-950/30 shadow-[0_0_15px_rgba(16,185,129,0.05)]',
    crimson: 'border-rose-900/40 bg-rose-950/20 hover:border-rose-800/70 hover:bg-rose-950/30 shadow-[0_0_15px_rgba(244,63,94,0.05)]',
    amber: 'border-amber-900/40 bg-amber-950/20 hover:border-amber-800/70 hover:bg-amber-950/30 shadow-[0_0_15px_rgba(245,158,11,0.05)]',
    cyan: 'border-cyan-900/40 bg-cyan-950/20 hover:border-cyan-800/70 hover:bg-cyan-950/30 shadow-[0_0_15px_rgba(6,182,212,0.05)]',
  };

  const iconColors = {
    default: 'text-[var(--text-muted)] bg-[var(--bg-surface-elevated)] border border-[var(--border-default)]',
    emerald: 'text-emerald-400 bg-emerald-950/60 border border-emerald-800/40',
    crimson: 'text-rose-400 bg-rose-950/60 border border-rose-800/40',
    amber: 'text-amber-400 bg-amber-950/60 border border-amber-800/40',
    cyan: 'text-cyan-400 bg-cyan-950/60 border border-cyan-800/40',
  };

  return (
    <div
      className={cn(
        'relative rounded-lg p-4 border transition-all duration-200 ease-out backdrop-blur-md interactive-card select-none',
        variantStyles[variant],
        className
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-medium uppercase tracking-wider text-[var(--text-muted)] flex items-center gap-1.5 font-mono">
          {title}
        </span>
        <div className="flex items-center gap-1.5">
          {badge}
          {Icon && (
            <div className={cn('p-1.5 rounded-md text-xs', iconColors[variant])}>
              <Icon className="w-3.5 h-3.5" />
            </div>
          )}
        </div>
      </div>

      <div className="flex items-baseline justify-between gap-2">
        <div className="text-2xl font-bold font-mono tracking-tight text-[var(--text-primary)] tabular-nums">
          {value}
        </div>
        {trendValue && (
          <span
            className={cn(
              'text-xs font-mono font-medium flex items-center gap-0.5 tabular-nums',
              trend === 'up' && 'text-emerald-400',
              trend === 'down' && 'text-rose-400',
              trend === 'neutral' && 'text-[var(--text-muted)]'
            )}
          >
            {trend === 'up' ? '▲' : trend === 'down' ? '▼' : '•'} {trendValue}
          </span>
        )}
      </div>

      {subtitle && (
        <div className="mt-1.5 text-xs text-[var(--text-muted)] font-mono flex items-center justify-between">
          {subtitle}
        </div>
      )}
    </div>
  );
};
